import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { JsonLdScript } from "@/app/_seo/JsonLdScript";
import { DEFAULT_OG_IMAGE_URL, absolutePublicUrl } from "@/app/_utils/constants";

export const INDEX_ROBOTS = "index,follow,max-image-preview:large";
export const NOINDEX_ROBOTS = "noindex,follow";

type SeoHelmetProps = {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  indexable?: boolean;
  jsonLd?: unknown;
};

function upsertMetaByName(name: string, content: string) {
  const nodes = Array.from(document.head.querySelectorAll(`meta[name="${name}"]`));
  let el = nodes[0] as HTMLMetaElement | undefined;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.content = content;
  nodes.slice(1).forEach((node) => node.remove());
}

function upsertMetaByProperty(property: string, content: string) {
  const nodes = Array.from(document.head.querySelectorAll(`meta[property="${property}"]`));
  let el = nodes[0] as HTMLMetaElement | undefined;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
  nodes.slice(1).forEach((node) => node.remove());
}

function upsertCanonical(href: string) {
  const nodes = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));
  let el = nodes[0] as HTMLLinkElement | undefined;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
  nodes.slice(1).forEach((node) => node.remove());
}

export function SeoHelmet({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE_URL,
  ogImageAlt,
  ogTitle,
  ogDescription,
  ogType = "website",
  indexable = true,
  jsonLd,
}: SeoHelmetProps) {
  const url = absolutePublicUrl(canonicalPath);
  const socialTitle = ogTitle ?? title;
  const socialDescription = ogDescription ?? description;
  const robots = indexable ? INDEX_ROBOTS : NOINDEX_ROBOTS;
  const imageAlt = ogImageAlt ?? socialTitle;

  // Upsert head tags so shell homepage meta cannot stick around next to page tags.
  useEffect(() => {
    document.documentElement.lang = "en";
    document.title = title;
    upsertMetaByName("description", description);
    upsertMetaByName("robots", robots);
    upsertCanonical(url);
    upsertMetaByProperty("og:title", socialTitle);
    upsertMetaByProperty("og:description", socialDescription);
    upsertMetaByProperty("og:url", url);
    upsertMetaByProperty("og:site_name", "Underground Overground Comedy");
    upsertMetaByProperty("og:image", ogImage);
    upsertMetaByProperty("og:image:width", "1200");
    upsertMetaByProperty("og:image:height", "630");
    upsertMetaByProperty("og:image:alt", imageAlt);
    upsertMetaByProperty("og:locale", "en_US");
    upsertMetaByProperty("og:type", ogType);
    upsertMetaByName("twitter:card", "summary_large_image");
    upsertMetaByName("twitter:title", socialTitle);
    upsertMetaByName("twitter:description", socialDescription);
    upsertMetaByName("twitter:image", ogImage);
  }, [
    title,
    description,
    robots,
    url,
    socialTitle,
    socialDescription,
    ogImage,
    imageAlt,
    ogType,
  ]);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content={robots} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={socialTitle} />
        <meta property="og:description" content={socialDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content="Underground Overground Comedy" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={imageAlt} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content={ogType} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={socialTitle} />
        <meta name="twitter:description" content={socialDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <JsonLdScript data={jsonLd} />
    </>
  );
}

export function NoIndexHelmet({
  title,
  description,
  canonicalPath,
}: {
  title: string;
  description: string;
  canonicalPath: string;
}) {
  return (
    <SeoHelmet
      title={title}
      description={description}
      canonicalPath={canonicalPath}
      indexable={false}
    />
  );
}
